// src/components/messaging/FloatingMessaging/FloatingMessagingDock.jsx - VERSION COMPLÈTE MODIFIÉE
import React, { useState, useRef, useEffect, useMemo } from 'react'
import { CButton, CSpinner, CModal, CModalBody, CModalHeader, CModalTitle } from '@coreui/react'
import CIcon from '@coreui/icons-react'
import {
  cilEnvelopeOpen,
  cilX,
  cilSearch,
  cilUserPlus,
  cilStar,
  cilReload,
  cilPeople,
  cilPlus,
} from '@coreui/icons'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import Avatar from '../../components/shared/Avatar'
import './FloatingMessagingDock.scss'

// Aperçu du dernier message dans la liste.
// - Les retours à la ligne et espaces multiples sont écrasés : sans ça, un
//   message multi-ligne fait exploser la hauteur de la rangée.
// - La chaîne est bornée pour ne pas injecter un pavé dans le DOM, mais SANS
//   ajouter de « ... » : l'ellipse est posée par le CSS, à la largeur réelle de
//   la colonne. Un compte de caractères ne connaît pas les pixels (un emoji ou
//   un « W » ne fait pas la largeur d'un « i »), il ne peut pas tomber juste.
const PREVIEW_MAX_CHARS = 200
const formatMessagePreview = (content) =>
  String(content || '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, PREVIEW_MAX_CHARS)

const FloatingMessagingDock = ({ messagingState }) => {
  const {
    isDockOpen,
    setIsDockOpen,
    recentConversations,
    totalUnread,
    openConversation,
    searchUsers,
    createConversation,
    loading,
    forceReloadConversations,
  } = messagingState

  const currentUser = useSelector((state) => state.user?.user)
  const { t, i18n } = useTranslation()

  // 🆕 État local pour le badge avec animation
  const [badgeAnimation, setBadgeAnimation] = useState(false)
  const [lastUnreadCount, setLastUnreadCount] = useState(totalUnread)

  // États pour le filtrage des conversations
  const [filterQuery, setFilterQuery] = useState('')

  // États pour le modal de nouvelle conversation
  const [showNewConversationModal, setShowNewConversationModal] = useState(false)
  const [userSearchQuery, setUserSearchQuery] = useState('')
  const [userSearchResults, setUserSearchResults] = useState([])
  const [isSearchingUsers, setIsSearchingUsers] = useState(false)
  const [creatingConversation, setCreatingConversation] = useState(false)

  // Pagination conversations supprimée : on charge tout d'un coup (limit=100)

  const searchTimeoutRef = useRef(null)
  const conversationsListRef = useRef(null)

  // 🆕 Effet pour animer le badge quand il change
  useEffect(() => {
    if (totalUnread !== lastUnreadCount && totalUnread > 0) {
      setBadgeAnimation(true)
      const timer = setTimeout(() => setBadgeAnimation(false), 600)
      setLastUnreadCount(totalUnread)
      return () => clearTimeout(timer)
    }
    setLastUnreadCount(totalUnread)
  }, [totalUnread, lastUnreadCount])

  // 🆕 Écouter les événements de mise à jour des notifications
  useEffect(() => {
    const handleUnreadUpdate = (event) => {
      console.log('🔔 Événement unread update reçu:', event.detail)
      // Le hook useFloatingMessaging se chargera de la mise à jour
      // Ceci est juste pour les logs de debug
    }

    window.addEventListener('messagingUnreadUpdate', handleUnreadUpdate)
    return () => window.removeEventListener('messagingUnreadUpdate', handleUnreadUpdate)
  }, [])

  // FONCTION 1 : Filtrage intelligent des conversations
  const filteredConversations = useMemo(() => {
    if (!filterQuery || filterQuery.length < 1) {
      return recentConversations
    }

    const query = filterQuery.toLowerCase().trim()

    return recentConversations.filter((conversation) => {
      // Recherche dans le nom du groupe ou du mentorat
      if (
        (conversation.type === 'group' || conversation.type === 'mentoring') &&
        conversation.name
      ) {
        if (conversation.name.toLowerCase().includes(query)) {
          return true
        }
      }

      // Recherche dans les noms des membres
      if (conversation.members) {
        const memberMatch = conversation.members.some((member) => {
          if (member.id === currentUser?.id) return false

          const fullName = `${member.firstName || ''} ${member.lastName || ''}`.toLowerCase()
          const firstName = (member.firstName || '').toLowerCase()
          const lastName = (member.lastName || '').toLowerCase()
          const email = (member.email || '').toLowerCase()

          return (
            fullName.includes(query) ||
            firstName.includes(query) ||
            lastName.includes(query) ||
            email.includes(query)
          )
        })

        if (memberMatch) return true
      }

      // Recherche dans le contenu du dernier message
      if (conversation.messages && conversation.messages.length > 0) {
        const lastMessage = conversation.messages[0]
        if (lastMessage.content && lastMessage.content.toLowerCase().includes(query)) {
          return true
        }
      }

      return false
    })
  }, [recentConversations, filterQuery, currentUser?.id])

  // FONCTION 2 : Recherche d'utilisateurs pour nouvelle conversation
  const searchUsersForNewConversation = async (query) => {
    if (!query || query.length < 2) {
      setUserSearchResults([])
      return
    }

    setIsSearchingUsers(true)

    try {
      const users = await searchUsers(query)

      // Exclure les utilisateurs avec qui on a déjà une conversation
      const existingUserIds = new Set()
      recentConversations.forEach((conv) => {
        if (conv.type === 'private' && conv.members) {
          conv.members.forEach((member) => {
            if (member.id !== currentUser?.id) {
              existingUserIds.add(member.id)
            }
          })
        }
      })

      const newUsers = users.filter((user) => !existingUserIds.has(user.id))
      setUserSearchResults(newUsers)
    } catch (error) {
      console.error('Erreur recherche utilisateurs:', error)
      setUserSearchResults([])
    } finally {
      setIsSearchingUsers(false)
    }
  }

  // Debounce pour la recherche d'utilisateurs
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchUsersForNewConversation(userSearchQuery)
    }, 300)

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [userSearchQuery])

  // Toutes les conversations sont chargées d'un coup (limit=100), pas de "load more"

  // Fonction de debug pour forcer le rechargement
  const handleForceReload = async () => {
    console.log("🔄 DEBUG: Force reload demandé par l'utilisateur")
    try {
      await forceReloadConversations()
      console.log('✅ DEBUG: Rechargement terminé')
    } catch (error) {
      console.error('❌ DEBUG: Erreur lors du rechargement:', error)
    }
  }

  // Toggle dock
  const toggleDock = () => {
    setIsDockOpen(!isDockOpen)
    if (!isDockOpen) {
      setFilterQuery('')
    }
  }

  // Ouvrir le modal de nouvelle conversation
  const handleOpenNewConversationModal = () => {
    setShowNewConversationModal(true)
    setUserSearchQuery('')
    setUserSearchResults([])
  }

  // Fermer le modal de nouvelle conversation
  const handleCloseNewConversationModal = () => {
    setShowNewConversationModal(false)
    setUserSearchQuery('')
    setUserSearchResults([])
  }

  // Créer une nouvelle conversation
  const handleCreateConversation = async (user) => {
    if (creatingConversation) return

    setCreatingConversation(true)

    try {
      await createConversation([user.id], 'private')
      handleCloseNewConversationModal()
    } catch (error) {
      console.error('Erreur création conversation:', error)
    } finally {
      setCreatingConversation(false)
    }
  }

  // Ouvrir conversation existante
  const handleOpenConversation = async (conversation) => {
    try {
      await openConversation(conversation.id)
    } catch (error) {
      console.error('Erreur ouverture conversation:', error)
    }
  }

  // Clear filter
  const handleClearFilter = () => {
    setFilterQuery('')
  }

  // Format temps
  const formatLastMessageTime = (timestamp) => {
    if (!timestamp) return ''

    const date = new Date(timestamp)
    const now = new Date()
    const diff = now - date

    if (diff < 60000) return t('messaging:time.now')
    if (diff < 3600000)
      return t('messaging:time.minutes_short', { count: Math.floor(diff / 60000) })
    if (diff < 86400000)
      return t('messaging:time.hours_short', { count: Math.floor(diff / 3600000) })
    // Locale de l'UI et non 'fr-FR' figé : sinon une interface en anglais
    // afficherait quand même « 13 août ».
    return date.toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' })
  }

  // Nom conversation
  const getConversationDisplayName = (conversation) => {
    if (conversation.type === 'group') {
      return conversation.name || t('messaging:conversation.group_fallback')
    }

    const otherMember = conversation.members?.find((m) => m.id !== currentUser?.id)
    const otherName = otherMember
      ? `${otherMember.firstName} ${otherMember.lastName}`
      : t('messaging:conversation.unknown_user')

    // Pour un mentorat : préfixer le nom de l'autre membre par ⭐
    // pour distinguer cette conversation des DM standards.
    if (conversation.type === 'mentoring') {
      return `⭐ ${otherName}`
    }
    return otherName
  }

  // Avatar conversation
  const getConversationAvatar = (conversation) => {
    if (conversation.type === 'group') {
      const initial = (conversation.name || 'G')[0].toUpperCase()
      return <div className="group-avatar-dock">{initial}</div>
    }

    const otherMember = conversation.members?.find((m) => m.id !== currentUser?.id)
    if (!otherMember) {
      return <div className="unknown-avatar-dock">?</div>
    }
    if (conversation.type === 'mentoring') {
      return (
        <div style={{ position: 'relative' }}>
          <Avatar user={otherMember} size={48} />
          <span
            title={t('messaging:conversation.mentoring_badge')}
            style={{
              position: 'absolute',
              bottom: -4,
              right: -4,
              background: '#fff',
              borderRadius: '50%',
              fontSize: '0.85rem',
              lineHeight: 1,
              padding: '1px 3px',
              border: '1px solid rgba(0,0,0,0.08)',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            ⭐
          </span>
        </div>
      )
    }
    return <Avatar user={otherMember} size={48} />
  }

  // Highlight des termes de recherche
  const highlightSearchTerm = (text, searchTerm) => {
    if (!searchTerm || !text) return text

    const regex = new RegExp(`(${searchTerm})`, 'gi')
    const parts = text.split(regex)

    return parts.map((part, index) =>
      regex.test(part) ? (
        <span key={index} className="search-highlight">
          {part}
        </span>
      ) : (
        part
      ),
    )
  }

  if (!currentUser) return null

  return (
    <div className="floating-messaging-dock">
      {/* 🆕 Bouton principal premium avec badge amélioré */}
      <div className="dock-trigger-premium">
        <button
          className={`dock-button-premium ${badgeAnimation ? 'badge-animation' : ''}`}
          onClick={toggleDock}
          aria-label={t('messaging:dock.open_aria')}
        >
          <CIcon icon={cilEnvelopeOpen} />
          {totalUnread > 0 && (
            <span
              className={`unread-badge-premium ${badgeAnimation ? 'animate' : ''} ${totalUnread > 10 ? 'high-count' : ''}`}
              data-count={totalUnread > 10 ? 'high' : 'normal'}
            >
              {totalUnread > 99 ? '99+' : totalUnread}
            </span>
          )}
        </button>
      </div>

      {/* Panneau premium */}
      {isDockOpen && (
        <div className="dock-panel-premium">
          {/* En-tête moderne */}
          <div className="dock-header-premium">
            <div className="dock-title-section">
              <h3 className="dock-title-text">{t('messaging:dock.title')}</h3>
              <span className="dock-subtitle">
                {filterQuery
                  ? t('messaging:dock.result_count', { count: filteredConversations.length })
                  : t('messaging:dock.conversation_count', { count: recentConversations.length })}
                {totalUnread > 0 && (
                  <span className="total-unread-indicator">
                    {t('messaging:dock.unread_indicator', { count: totalUnread })}
                  </span>
                )}
              </span>
            </div>
            <div className="dock-actions-premium">
              <button
                className="dock-action-btn-premium reload-btn"
                onClick={handleForceReload}
                title={t('messaging:dock.reload')}
                disabled={loading}
              >
                {loading ? <CSpinner size="sm" /> : <CIcon icon={cilReload} />}
              </button>
              <button
                className="dock-action-btn-premium close-dock"
                onClick={toggleDock}
                title={t('common:close')}
              >
                <CIcon icon={cilX} />
              </button>
            </div>
          </div>

          {/* Section de filtrage des conversations */}
          <div className="filter-section-premium">
            <div className="filter-input-container">
              <div className="filter-icon">
                <CIcon icon={cilSearch} />
              </div>
              <input
                type="text"
                placeholder={t('messaging:dock.filter_placeholder')}
                value={filterQuery}
                onChange={(e) => setFilterQuery(e.target.value)}
                className="filter-input-premium"
              />
              {filterQuery && (
                <button
                  className="filter-clear-btn"
                  onClick={handleClearFilter}
                  title={t('messaging:dock.clear_filter')}
                >
                  <CIcon icon={cilX} />
                </button>
              )}
            </div>
          </div>

          {/* Bouton Nouvelle Conversation - VERSION COMPACTE */}
          <div className="new-conversation-section-compact">
            <button
              className="new-conversation-btn-compact"
              onClick={handleOpenNewConversationModal}
              disabled={creatingConversation}
            >
              <div className="compact-icon">
                <CIcon icon={cilPlus} />
              </div>
              <span className="compact-text">{t('messaging:dock.new_conversation')}</span>
            </button>
          </div>

          {/* Liste des conversations premium */}
          <div className="conversations-section-premium">
            {loading ? (
              <div className="conversations-loading-state">
                <CSpinner />
                <span>{t('messaging:dock.loading_conversations')}</span>
              </div>
            ) : filteredConversations.length > 0 ? (
              <div className="conversations-list-premium" ref={conversationsListRef}>
                {filteredConversations.map((conversation) => {
                  const displayName = getConversationDisplayName(conversation)
                  const lastMessage = conversation.messages?.[0]
                  const hasUnread = conversation.unreadCount > 0

                  return (
                    <div
                      key={conversation.id}
                      className={`conversation-item-premium ${hasUnread ? 'has-unread' : ''}`}
                      onClick={() => handleOpenConversation(conversation)}
                    >
                      <div className="conversation-avatar-container">
                        {getConversationAvatar(conversation)}
                        {conversation.type === 'group' && (
                          <div className="conversation-type-indicator">
                            <CIcon icon={cilPeople} />
                          </div>
                        )}
                      </div>

                      <div className="conversation-content-premium">
                        <div className="conversation-header-row">
                          <span className="conversation-name-premium">
                            {highlightSearchTerm(displayName, filterQuery)}
                          </span>
                          {lastMessage && (
                            <span className="conversation-time-premium">
                              {formatLastMessageTime(
                                lastMessage.created_at || lastMessage.createdAt,
                              )}
                            </span>
                          )}
                        </div>

                        <div className="conversation-preview-row">
                          <div className="conversation-last-message">
                            {lastMessage ? (
                              <span className={hasUnread ? 'unread-message' : 'read-message'}>
                                {lastMessage.sender_id === currentUser.id && (
                                  <span className="conversation-preview-prefix">
                                    {t('messaging:conversation.preview_prefix_self')}{' '}
                                  </span>
                                )}
                                {highlightSearchTerm(
                                  formatMessagePreview(lastMessage.content),
                                  filterQuery,
                                )}
                              </span>
                            ) : (
                              <span className="new-conversation-text">
                                {t('messaging:dock.new_conversation')}
                              </span>
                            )}
                          </div>

                          {hasUnread && (
                            <div className="conversation-unread-badge">
                              {conversation.unreadCount > 9 ? '9+' : conversation.unreadCount}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="conversations-empty-state">
                <div className="empty-state-icon">
                  <CIcon icon={filterQuery ? cilSearch : cilStar} />
                </div>
                <div className="empty-state-title">
                  {filterQuery
                    ? t('messaging:dock.no_result_title')
                    : t('messaging:dock.empty_title')}
                </div>
                <div className="empty-state-subtitle">
                  {filterQuery
                    ? t('messaging:dock.no_result_subtitle')
                    : t('messaging:dock.empty_subtitle')}
                </div>
                {!filterQuery && (
                  <button
                    className="empty-state-action-btn"
                    onClick={handleOpenNewConversationModal}
                  >
                    <CIcon icon={cilPlus} />
                    <span>{t('messaging:dock.empty_action')}</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal Nouvelle Conversation */}
      <CModal
        visible={showNewConversationModal}
        onClose={handleCloseNewConversationModal}
        className="new-conversation-modal"
        backdrop="static"
      >
        <CModalHeader className="new-conversation-modal-header">
          <CModalTitle>{t('messaging:dock.new_conversation')}</CModalTitle>
        </CModalHeader>
        <CModalBody className="new-conversation-modal-body">
          {/* Recherche d'utilisateurs */}
          <div className="user-search-section">
            <div className="user-search-input-container">
              <div className="user-search-icon">
                <CIcon icon={cilSearch} />
              </div>
              <input
                type="text"
                placeholder={t('messaging:new_conversation_modal.search_placeholder')}
                value={userSearchQuery}
                onChange={(e) => setUserSearchQuery(e.target.value)}
                className="user-search-input"
                autoFocus
              />
              {isSearchingUsers && (
                <div className="user-search-loading">
                  <CSpinner size="sm" />
                </div>
              )}
            </div>

            {/* Résultats de recherche */}
            {userSearchQuery.length >= 2 && (
              <div className="user-search-results">
                {isSearchingUsers ? (
                  <div className="user-search-loading-state">
                    <CSpinner />
                    <span>{t('messaging:new_conversation_modal.searching')}</span>
                  </div>
                ) : userSearchResults.length > 0 ? (
                  <div className="user-search-results-list">
                    {userSearchResults.map((user) => (
                      <div
                        key={user.id}
                        className="user-search-result-item"
                        onClick={() => handleCreateConversation(user)}
                      >
                        <div className="user-search-result-avatar">
                          <Avatar user={user} size={40} />
                        </div>
                        <div className="user-search-result-info">
                          <div className="user-search-result-name">
                            {user.firstName} {user.lastName}
                          </div>
                          {user.position && (
                            <div className="user-search-result-position">{user.position}</div>
                          )}
                          {user.company && (
                            <div className="user-search-result-company">{user.company}</div>
                          )}
                        </div>
                        {creatingConversation && (
                          <div className="user-search-result-loading">
                            <CSpinner size="sm" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="user-search-no-results">
                    <div className="no-results-icon">
                      <CIcon icon={cilSearch} />
                    </div>
                    <div className="no-results-text">
                      {t('messaging:new_conversation_modal.no_results')}
                    </div>
                    <div className="no-results-hint">
                      {t('messaging:new_conversation_modal.no_results_hint')}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* État initial */}
            {userSearchQuery.length < 2 && (
              <div className="user-search-initial-state">
                <div className="initial-state-icon">
                  <CIcon icon={cilUserPlus} />
                </div>
                <div className="initial-state-title">
                  {t('messaging:new_conversation_modal.initial_title')}
                </div>
                <div className="initial-state-subtitle">
                  {t('messaging:new_conversation_modal.initial_subtitle')}
                </div>
              </div>
            )}
          </div>
        </CModalBody>
      </CModal>
    </div>
  )
}

export default FloatingMessagingDock
